import { fg } from '@atlassian/jira-feature-gating';
import { memoizedFetchSiteUserPersonalization } from '@atlassian/jira-personalization-service';

import { getBillingInformationCCP } from './ccp';
import { getBillingInformationGraphql } from './commerce-shared-api';
import { IS_CCP_BILLING_ADMIN } from './constants';
import { getBillingInformationHAMS } from './hams';
import type { BillingInformationArgs, BillingInformation } from './types';

export const getBillingInformation = async ({
	cloudId,
	userId,
	billingSourceSystem,
	entitlementId,
	isSiteAdmin,
	productKey,
	relayEnvironment,
}: BillingInformationArgs): Promise<BillingInformation> => {
	let invoiceable: boolean | null = null;
	let transactionAccountId: string | null = null;
	let invoiceGroupId: string | null = null;
	let isBillingAdmin: boolean | null = null;

	if (!cloudId) {
		throw new Error('empty cloudId');
	}

	if (!productKey) {
		throw new Error('empty productKey');
	}

	// Once we roll out getBillingInformationGraphql will replace this controller
	if (fg('trial_edition_awareness_use_graphql_query')) {
		return getBillingInformationGraphql(cloudId, productKey, relayEnvironment);
	}

	if (billingSourceSystem === 'CCP') {
		if (!entitlementId) {
			throw new Error('empty entitlementId');
		}

		if (!userId) {
			throw new Error('empty userId');
		}

		// Billing Admins trait checks whether user is CCP billing admin on current site
		// This check helps us to control the load on CCP services and billing status will be checked
		// again inside CCP service to make sure that billing admin permission was not revoked since last trait refresh
		const traits = await memoizedFetchSiteUserPersonalization({ cloudId, userId });
		const ccpBillingAdminTrait = traits?.find((trait) => trait.name === IS_CCP_BILLING_ADMIN);

		if (ccpBillingAdminTrait?.value) {
			const response = await getBillingInformationCCP(entitlementId);

			invoiceable = response.invoiceable;

			// Billing admin check is skipped if site is not in trial/grace period so we have to make sure that null value
			// is persisted here so we can evaluated whether value is intetionally false or that check was just skipped
			isBillingAdmin =
				response.billingAdmins && !!response.billingAdmins.find(({ id = null }) => id === userId);
			transactionAccountId = response.transactionAccountId;
			invoiceGroupId = response.invoiceGroupId;
		} else {
			// Explicitly marking value to false
			isBillingAdmin = false;
		}
		// If billing source system is HAMS all site admins are also billing admin so all we need to do is simple siteAdmin check
		// all isBillingAdmin values are being explicitly set
	} else if (isSiteAdmin) {
		const response = await getBillingInformationHAMS(cloudId);
		invoiceable = response.invoiceable;
		isBillingAdmin = true;
	} else {
		isBillingAdmin = false;
	}

	return {
		invoiceable,
		transactionAccountId,
		invoiceGroupId,
		isBillingAdmin,
	};
};
